import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Fab
} from '@mui/material';
import { 
  Warehouse, 
  Add, 
  Search,
  Inventory,
  LocalShipping,
  Home,
  Edit,
  Delete,
  Save
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function MUIBasicsPage() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Warehouse sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            MUI Component Testing
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Material-UI Components
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Step 2: Testing basic MUI components and interactions
          </Typography>
        </Paper>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 3
        }}>
          
          {/* Buttons Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Buttons
              </Typography>
              <Box sx={{ '& button': { m: 1 } }}>
                <Button 
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => alert('Add Product clicked!')}
                >
                  Add Product
                </Button>
                <Button 
                  variant="outlined"
                  startIcon={<Search />}
                  onClick={() => alert('Search clicked!')}
                >
                  Search
                </Button>
                <Button 
                  variant="text"
                  onClick={() => alert('Cancel clicked!')}
                >
                  Cancel
                </Button>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Icon Buttons:
              </Typography>
              <Box sx={{ '& button': { m: 0.5 } }}>
                <IconButton 
                  color="primary"
                  onClick={() => alert('Edit clicked!')}
                >
                  <Edit />
                </IconButton>
                <IconButton 
                  color="error"
                  onClick={() => alert('Delete clicked!')}
                >
                  <Delete />
                </IconButton>
                <IconButton 
                  color="success"
                  onClick={() => alert('Save clicked!')}
                >
                  <Save />
                </IconButton>
              </Box>
            </CardContent>
          </Card>

          {/* Text Fields Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Input Fields
              </Typography>
              <Box sx={{ '& > :not(style)': { m: 1, width: 'calc(100% - 16px)' } }}>
                <TextField
                  label="Product Name"
                  variant="outlined"
                  size="small"
                  onChange={(e) => console.log('Product Name:', e.target.value)}
                />
                <TextField
                  label="Quantity"
                  type="number"
                  variant="filled"
                  size="small"
                  onChange={(e) => console.log('Quantity:', e.target.value)}
                />
                <TextField
                  label="Description"
                  variant="standard"
                  multiline
                  rows={2}
                  onChange={(e) => console.log('Description:', e.target.value)}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Status Chips Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Chips
              </Typography>
              <Box sx={{ '& > :not(style)': { m: 0.5 } }}>
                <Chip 
                  label="In Stock" 
                  color="success" 
                  onClick={() => console.log('In Stock clicked')}
                />
                <Chip 
                  label="Low Stock" 
                  color="warning" 
                  onClick={() => console.log('Low Stock clicked')}
                />
                <Chip 
                  label="Out of Stock" 
                  color="error" 
                  onClick={() => console.log('Out of Stock clicked')}
                />
                <Chip 
                  label="Processing" 
                  color="info" 
                  onClick={() => console.log('Processing clicked')}
                />
              </Box>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Deletable Chips:
              </Typography>
              <Box sx={{ '& > :not(style)': { m: 0.5 } }}>
                <Chip 
                  label="Tag 1" 
                  onDelete={() => console.log('Delete Tag 1')}
                />
                <Chip 
                  label="Tag 2" 
                  variant="outlined"
                  onDelete={() => console.log('Delete Tag 2')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Icons Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Icons & FAB
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Inventory color="primary" />
                <LocalShipping color="secondary" />
                <Warehouse color="action" />
                <Add color="success" />
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>
                Floating Action Button:
              </Typography>
              <Fab 
                color="primary" 
                size="small"
                onClick={() => alert('FAB clicked!')}
              >
                <Add />
              </Fab>
            </CardContent>
          </Card>

        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Interactive Test Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Click buttons to see alerts<br/>
            • Type in text fields to see console logs<br/>
            • Click chips to see console messages<br/>
            • All components are fully interactive!
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Button 
              component={Link}
              to="/react-hook-form"
              variant="contained"
              sx={{ mr: 2 }}
            >
              Next: React Hook Form
            </Button>
            <Button 
              component={Link}
              to="/"
              variant="outlined"
            >
              Back to Home
            </Button>
          </Box>
        </Paper>

      </Container>
    </ThemeProvider>
  );
}

export default MUIBasicsPage;
